import { eventFactoryCollection } from '../event-factory'
import type { Analytics } from './analytics-multiplatform-sdk'
import { AnalyticsServiceFactory } from './analytics-multiplatform-sdk'
import { MParticleDelegate } from './delegate'
import { BLOCKED_MPARTICLE_EVENT_TYPES } from './types'
import type { MParticleBatch, MParticleEvent, MParticleSDK } from './types'
import type {
  AnalyticsEventName,
  AnalyticsVendor,
  PickAnalyticsEventAttributes,
} from '@/tools/analytics/types'
import { getConfig } from '@/tools/config'
import { serviceConsentCategory } from '@/tools/cookies-blocker/service/constants'
import type { CookieCategory } from '@/tools/cookies-blocker/types'

export class mParticle implements AnalyticsVendor {
  public name = 'mParticle'
  public consentCategory: CookieCategory = serviceConsentCategory.mParticle
  public isInitialized = false

  private analyticsService: Analytics.AnalyticsService | null = null

  public async init() {
    const mParticleSDKInstance = await this.lazyLoadModule()

    const { version, mParticleId, brazeId, env } = getConfig()

    const isProduction = env === 'prod'
    const logLevel = isProduction ? 'none' : 'warning'
    const mParticleConfig = {
      isDevelopmentMode: !isProduction,
      logLevel,
      onCreateBatch: (batch: MParticleBatch) => {
        // the following removes Application State Transition (AST) events, except for those uploaded on first-run
        // First-run AST events are used by many server-side integrations to mark an "install" and are used by
        // mParticle to ensure there is a user profile created
        batch.events = batch.events.filter(
          (event: MParticleEvent) =>
            !Object.values(BLOCKED_MPARTICLE_EVENT_TYPES).includes(event.event_type) ||
            event.is_first_run
        )
        return batch
      },
    }

    // We need to lazy load brazeKit and brazeSDK because this package was being evaluated on the server side,
    // resulting in an error when the window object was not available (since window only exists in the browser).
    const brazeSDK = await this.loadBrazeSDK()
    const { default: brazeKit } = await this.loadBrazeKit()

    brazeKit.register(mParticleConfig)
    mParticleSDKInstance.init(mParticleId, mParticleConfig)
    brazeSDK.initialize(brazeId, {
      baseUrl: 'sdk.fra-01.braze.eu',
    })

    this.analyticsService = AnalyticsServiceFactory.create(
      new MParticleDelegate(mParticleSDKInstance),
      version
    )
    this.isInitialized = true
  }

  private async lazyLoadModule(): Promise<MParticleSDK> {
    const { default: mParticleSDK } = await import('@mparticle/web-sdk')
    return mParticleSDK
  }

  public canHandle = (eventName: AnalyticsEventName) =>
    Object.keys(eventFactoryCollection).includes(eventName)

  public trackEvent<EVENT_NAME extends AnalyticsEventName>(
    eventName: EVENT_NAME,
    attributes: PickAnalyticsEventAttributes<EVENT_NAME>
  ) {
    if (!this.analyticsService) {
      throw new Error('MParticle Analytics SDK is not initialized')
    }

    const factoryMethod = eventFactoryCollection[eventName as keyof typeof eventFactoryCollection]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const eventInstance = factoryMethod(attributes as any)
    this.analyticsService.track(eventInstance)
  }

  private async loadBrazeSDK() {
    const brazeSDK = await import('@braze/web-sdk')
    return brazeSDK
  }

  private async loadBrazeKit() {
    const brazeKit = await import('@mparticle/web-braze-kit')
    return brazeKit
  }
}
